import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useStorageUpload } from '@/db/useStorageUpload';
import { cn } from '@/lib/utils';
import { allBrandsState, allEquipmentTypesState, equipmentTypeFamiliesState, modelDocumentLibraryState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { documentTitle, isDirectLink, replacementPreview, supersessionRefusal } from '@rock/manuals';
import type { Model, ModelDocument, ModelId } from '@rock/types';
import { type FC, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { replaceDocument } from './documentEdits';
import { offerDocumentFile, offerDocumentUrl } from './documentOffers';
import { filterDocuments } from './documentSearch';
import { DocumentBrandChips } from './documentShared';
import { brandsById } from './modelBrand';

const log = logger('ReplaceDocumentDialog');

/** How many library matches the picker lists at once; the search narrows the rest. */
const PICK_LIMIT = 12;

type Mode = 'published' | 'offer';

/**
 * Replace a document with a newer revision: one already published, or a file or URL offered to one of the
 * models linking it, which reviews as that revision under the model's Candidates. On a model that links
 * both, the revision is current and this document superseded; where only this one reaches, it stays.
 */
export const ReplaceDocumentDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    doc: ModelDocument;
    linkedModels: Model[];
}> = ({ open, onOpenChange, doc, linkedModels }) => {
    const [mode, setMode] = useState<Mode>('published');
    const [busy, setBusy] = useState(false);
    return (
        <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Replace with newer revision</DialogTitle>
                    <DialogDescription>
                        Models that get the newer revision switch to it, and this one stays in their library,
                        superseded. Models it does not reach keep this one.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant={mode === 'published' ? 'default' : 'outline'}
                        onClick={() => setMode('published')}
                        disabled={busy}
                    >
                        A published document
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant={mode === 'offer' ? 'default' : 'outline'}
                        onClick={() => setMode('offer')}
                        disabled={busy}
                    >
                        A file or URL
                    </Button>
                </div>
                {mode === 'published' ? (
                    <PickPublished
                        doc={doc}
                        linkedModels={linkedModels}
                        busy={busy}
                        setBusy={setBusy}
                        onDone={() => onOpenChange(false)}
                    />
                ) : (
                    <OfferRevision
                        doc={doc}
                        linkedModels={linkedModels}
                        busy={busy}
                        setBusy={setBusy}
                        onDone={() => onOpenChange(false)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

interface ModeProps {
    doc: ModelDocument;
    linkedModels: Model[];
    busy: boolean;
    setBusy: (busy: boolean) => void;
    onDone: () => void;
}

const PickPublished: FC<ModeProps> = ({ doc, linkedModels, busy, setBusy, onDone }) => {
    const documents = useAsyncAtomValue(modelDocumentLibraryState, []);
    const brands = useAsyncAtomValue(allBrandsState, []);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState<ModelDocument>();
    const options = useMemo(
        () =>
            filterDocuments(
                documents.filter((other) => other.id !== doc.id),
                {
                    filter: 'all',
                    query,
                    linkedByNumber: new Set(),
                    brandsById: brandsById(brands),
                    typeTitles: new Map(types.map((type) => [type.id, type.title])),
                },
            ).slice(0, PICK_LIMIT),
        [documents, doc.id, query, brands, types],
    );
    const refusal = picked && supersessionRefusal(picked, doc);
    const preview = picked && !refusal && replacementPreview(picked, linkedModels, families);

    const save = async () => {
        if (!picked) return;
        setBusy(true);
        try {
            await replaceDocument(doc, picked.id);
            toast.success('Replaced', { description: 'The linked models switch over in a moment.' });
            onDone();
        } catch (err) {
            log.error('Replacing a document failed', { err, docId: doc.id, successorId: picked.id });
            toast.error('Unable to replace', { description: err instanceof Error ? err.message : `${err}` });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="space-y-2">
                <Input
                    placeholder="Search title, brand, type, model number…"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    disabled={busy}
                    autoFocus
                />
                <div className="max-h-64 space-y-1 overflow-auto">
                    {options.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => setPicked(option)}
                            className={cn(
                                'flex w-full flex-wrap items-center gap-2 rounded-md border p-2 text-left text-sm hover:bg-muted/50',
                                picked?.id === option.id && 'border-primary bg-muted',
                            )}
                        >
                            <span className="font-medium">{documentTitle(option)}</span>
                            <span className="font-mono text-xs text-muted-foreground">{option.id}</span>
                            <DocumentBrandChips brandIds={option.brandIds} />
                        </button>
                    ))}
                    {options.length === 0 && <div className="section-description">No document matches.</div>}
                </div>
                {preview && <ReplacementPreviewLines moves={preview.moves} stays={preview.stays} />}
                {refusal && (
                    <div role="alert" className="text-sm text-amber-700">
                        {refusal}
                    </div>
                )}
            </div>
            <DialogFooter>
                <Button type="button" variant="outline" onClick={onDone} disabled={busy}>
                    Cancel
                </Button>
                <Button type="button" onClick={() => void save()} disabled={busy || !picked || !!refusal}>
                    {busy ? 'Replacing…' : 'Replace'}
                </Button>
            </DialogFooter>
        </>
    );
};

const ReplacementPreviewLines: FC<{ moves: Model[]; stays: Model[] }> = ({ moves, stays }) => (
    <div className="space-y-1 rounded-md bg-muted p-3 text-sm">
        <div>
            Switches {moves.length} linked model{moves.length === 1 ? '' : 's'} to the newer revision
            {moves.length > 0 ? `: ${modelNumbers(moves)}` : ''}.
        </div>
        <div>
            Keeps this one on {stays.length} model{stays.length === 1 ? '' : 's'}
            {stays.length > 0 ? `: ${modelNumbers(stays)}` : ''}.
        </div>
        <div className="text-xs text-muted-foreground">
            The newer revision's own rule reaches other models as usual.
        </div>
    </div>
);

const OfferRevision: FC<ModeProps> = ({ doc, linkedModels, busy, setBusy, onDone }) => {
    const { uploadFile } = useStorageUpload();
    const hosts = useMemo(() => hostModels(doc, linkedModels), [doc, linkedModels]);
    const [hostId, setHostId] = useState<ModelId | undefined>(hosts[0]?.id);
    const [file, setFile] = useState<File | null>(null);
    const [url, setUrl] = useState('');
    const host = hosts.find((model) => model.id === hostId);

    const offer = async () => {
        if (!host || (!file && !url.trim())) return;
        setBusy(true);
        try {
            if (file) await offerDocumentFile(host.id, file, uploadFile, { replaces: doc.id });
            else await offerDocumentUrl(host.id, url.trim(), { replaces: doc.id });
            toast.success(`Offered on ${host.modelCanonical}`, {
                description: 'Review it under that model’s Candidates; publishing it replaces this document.',
            });
            onDone();
        } catch (err) {
            log.error('Offering a revision failed', { err, docId: doc.id, modelId: host.id });
            toast.error('Unable to offer', { description: err instanceof Error ? err.message : `${err}` });
        } finally {
            setBusy(false);
        }
    };

    if (hosts.length === 0) {
        return (
            <div className="section-description">
                No model links this document. An offer is reviewed on a model: link one first, or pick a published
                document instead.
            </div>
        );
    }
    return (
        <>
            <div className="space-y-3">
                <div className="space-y-1">
                    <Label>Review it on</Label>
                    <Select value={hostId} onValueChange={(value) => setHostId(value as ModelId)} disabled={busy}>
                        <SelectTrigger className="w-[280px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {hosts.map((model) => (
                                <SelectItem key={model.id} value={model.id}>
                                    {model.modelDisplay ?? model.modelCanonical}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="revision-file">PDF</Label>
                    <Input
                        id="revision-file"
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                        disabled={busy || !!url.trim()}
                        className="h-8"
                    />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="revision-url">Or a URL</Label>
                    <Input
                        id="revision-url"
                        placeholder="https://…"
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        disabled={busy || !!file}
                    />
                </div>
                <div className="field-hint">
                    Rock verifies and stages it like any offer. Its brands, types and rule start from this document's
                    when you publish it.
                </div>
            </div>
            <DialogFooter>
                <Button type="button" variant="outline" onClick={onDone} disabled={busy}>
                    Cancel
                </Button>
                <Button type="button" onClick={() => void offer()} disabled={busy || (!file && !url.trim())}>
                    {busy ? 'Offering…' : 'Offer'}
                </Button>
            </DialogFooter>
        </>
    );
};

/** The models an offer can be reviewed on: direct linkers first, newest link first. */
function hostModels(doc: ModelDocument, linkedModels: Model[]): Model[] {
    const linkOf = (model: Model) => model.documents?.[doc.id];
    return [...linkedModels]
        .filter((model) => !model.archivedAt && !!linkOf(model))
        .sort((a, b) => {
            const left = linkOf(a);
            const right = linkOf(b);
            const direct = Number(!!right && isDirectLink(right)) - Number(!!left && isDirectLink(left));
            return direct || (right?.linkedAt ?? '').localeCompare(left?.linkedAt ?? '');
        });
}

function modelNumbers(models: Model[]): string {
    const shown = models.slice(0, 6).map((model) => model.modelCanonical);
    return models.length > shown.length
        ? `${shown.join(', ')} and ${models.length - shown.length} more`
        : shown.join(', ');
}
